import clsx from 'clsx';
import type React from 'react';
import { Icons } from '../../icons/Icon';
import styles from './inputText.module.css';

type InputTextProps = {
  noBorder?: boolean;
  value: string;
  name?: string;
  icon?: React.ReactNode;
  error?: string;
  errorMsg?: string;
  borderColor?: string;
  size?: number | 'auto';
  customStyle?: React.CSSProperties;
  isDisabled?: boolean;
  placeholder?: string;
  iconClick?: () => void | undefined;
  isClerable?: boolean;
  onChange: ({ value, name }: { value: string; name: string }) => void;
  type?: string;
  canBeFocused?: boolean;
  tooltip?: string;
  maxLength?: number | null;
  specialСharacter?: boolean;
  max?: number | null;
  min?: number | null;
  dataTestId?: string;
};

export const InputText = ({
  value = '',
  name = '',
  error = '',
  errorMsg = '',
  size,
  customStyle = {},
  borderColor = '',
  isDisabled = false,
  icon = null,
  onChange,
  placeholder = '',
  noBorder = false,
  iconClick,
  isClerable = false,
  type,
  canBeFocused = false,
  tooltip = '',
  maxLength = null,
  specialСharacter = false,
  max = null,
  min = null,
  dataTestId = '',
}: InputTextProps) => {
  const inputWrapperClass = clsx({
    [styles.inputWrapper]: true,
    [styles.disabled]: isDisabled,
    [styles.inputWrapperNoBorder]: noBorder,
    [styles.inputWrapperFocused]: canBeFocused,
  });

  const style = {
    width: size ? `${size}px` : '100%',
    ...customStyle,
  };

  const inputResetClass = clsx([styles.inputReset, styles.textContainer, styles.saigonInput], {
    'ui-state-bdisabled': isDisabled,
  });

  const clear = () => {
    onChange({ value: '', name });
  };

  const validateValue = (value: string) => {
    const trimmedValue = value.replace(/^\s+|\s+$/g, '');
    if (value !== trimmedValue) {
      onChange({ value: trimmedValue, name });
    }
  };

  return (
    <div
      className={styles.inputContainer}
      style={(typeof size === 'number' && { display: 'inline-block' }) || undefined}
    >
      <div
        className={inputWrapperClass}
        style={{
          maxWidth: size ? `${size}px` : '100%',
          ...customStyle,
          borderColor: errorMsg || error ? 'var(--error-color)' : (borderColor ?? 'initial'),
        }}
      >
        <input
          data-testid={dataTestId}
          maxLength={maxLength ?? undefined}
          title={tooltip}
          value={value}
          disabled={isDisabled}
          placeholder={placeholder}
          onBlur={() => validateValue(value)}
          id={name}
          name={name}
          max={max ?? undefined}
          min={min ?? undefined}
          type={type ?? 'text'}
          onChange={(e) => {
            let value = e.target.value.replace(/\s{2,}/g, ' ');
            if (maxLength) {
              value = value.slice(0, +maxLength);
            }
            if (specialСharacter) {
              if (!/^[a-zA-Z0-9]*$/.test(String(value)) || String(value).includes(' ')) {
                return;
              }
            }
            if (type === 'number' && max && +max < +value) {
              value = String(max);
            }
            onChange({ value, name });
          }}
          // TODO legacy admin styles compatibility
          className={inputResetClass}
          style={style}
        />
        {icon && !value && isClerable && (
          <span className={styles.iconWrapper} onClick={iconClick && iconClick}>
            <Icons icon={String(icon)} fill="#012854" color="#012854" />
          </span>
        )}
        {isClerable && value && (
          <span className={styles.iconWrapper} onClick={clear}>
            <Icons icon={'Xmark'} fill="#012854" color="#012854" />
          </span>
        )}
      </div>
      {error && (
        <span
          id="error-input-value"
          style={{ width: style.width }}
          className="tw-text-error tw-p-1 tw-text-[12px] tw-absolute"
        >
          {error}
        </span>
      )}
      {errorMsg && (
        <div id="error-input-value" style={{ width: style.width }} className="tw-text-error tw-p-1 tw-text-[12px]">
          {errorMsg}
        </div>
      )}
    </div>
  );
};
